/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { useIntl } from 'react-intl'

import { EuiAccordion, EuiBasicTable, useGeneratedHtmlId } from '@elastic/eui'
import type { EuiBasicTableColumn } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { AnyProject } from '@modules/project-api/types'
import FormattedRegionAndProvider from '@modules/project-components/FormattedRegionAndProvider'

import { useProjectCpsContext } from '../../ProjectCpsContext'

type Props = {
  selectedProjects: AnyProject[]
}

const SelectionSummaryAccordion: React.FunctionComponent<Props> = ({ selectedProjects }) => {
  const { formatMessage } = useIntl()
  const accordionId = useGeneratedHtmlId({ prefix: 'selectionSummary' })
  const { useListRegionsQuery } = useProjectCpsContext()
  const { data: regions } = useListRegionsQuery()

  const columns: Array<EuiBasicTableColumn<AnyProject>> = [
    {
      field: 'name',
      name: <FormattedMessage id='cps.review.column.projectName' defaultMessage='Project name' />,
      width: '40%',
    },
    {
      field: 'alias',
      name: <FormattedMessage id='cps.review.column.projectAlias' defaultMessage='Project alias' />,
      width: '35%',
    },
    {
      name: (
        <FormattedMessage
          id='projects.list.header.region'
          defaultMessage='Cloud provider & region'
        />
      ),
      render: ({ region_id }: AnyProject) => (
        <FormattedRegionAndProvider regionId={region_id} allRegions={regions} />
      ),
      width: '25%',
    },
  ]

  return (
    <EuiAccordion
      id={accordionId}
      buttonContent={
        <FormattedMessage
          id='cps.review.viewSelectionSummary'
          defaultMessage='View selection summary'
        />
      }
      data-test-subj='cps-selectionSummaryAccordion'
      paddingSize='m'
    >
      <EuiBasicTable
        tableLayout='auto'
        items={selectedProjects}
        columns={columns}
        rowHeader='name'
        tableCaption={formatMessage({
          id: 'cps.review.tableCaption',
          defaultMessage: 'Selected projects summary',
        })}
        css={{
          maxHeight: '230px',
          overflowY: 'auto',
        }}
      />
    </EuiAccordion>
  )
}

export default SelectionSummaryAccordion
